import { useParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { usePageTitle } from '../../hooks/usePageTitle'
import { emailAccountsApi, emailTemplatesApi } from '../../api/settings'
import { PageSpinner } from '../../components/ui/Spinner'
import { Modal } from '../../components/ui/Modal'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { Plus, Pencil, Trash2, CheckCircle, XCircle, Loader } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'

const PROVIDER_PRESETS = [
  {
    id: 'gmail', label: 'Gmail',
    smtpHost: 'smtp.gmail.com', smtpPort: 587,
    imapHost: 'imap.gmail.com', imapPort: 993,
    helpUrl: 'https://support.google.com/accounts/answer/185833',
    helpLabel: 'How to create a Gmail App Password',
  },
  {
    id: 'outlook', label: 'Outlook 365',
    smtpHost: 'smtp.office365.com', smtpPort: 587,
    imapHost: 'outlook.office365.com', imapPort: 993,
    helpUrl: 'https://support.microsoft.com/en-us/office/pop-imap-and-smtp-settings-8361e398-8af4-4e97-b147-6c6c4ac95353',
    helpLabel: 'How to enable SMTP/IMAP in Outlook',
  },
  {
    id: 'yahoo', label: 'Yahoo',
    smtpHost: 'smtp.mail.yahoo.com', smtpPort: 587,
    imapHost: 'imap.mail.yahoo.com', imapPort: 993,
    helpUrl: 'https://help.yahoo.com/kb/generate-third-party-passwords-sln15241.html',
    helpLabel: 'How to generate a Yahoo App Password',
  },
  {
    id: 'icloud', label: 'iCloud',
    smtpHost: 'smtp.mail.me.com', smtpPort: 587,
    imapHost: 'imap.mail.me.com', imapPort: 993,
    helpUrl: 'https://support.apple.com/en-us/102654',
    helpLabel: 'How to set up an iCloud app-specific password',
  },
  {
    id: 'zoho', label: 'Zoho',
    smtpHost: 'smtp.zoho.com', smtpPort: 587,
    imapHost: 'imap.zoho.com', imapPort: 993,
    helpUrl: 'https://www.zoho.com/mail/help/imap-access.html',
    helpLabel: 'How to enable IMAP/SMTP access in Zoho Mail',
  },
  { id: 'custom', label: 'Custom', smtpHost: '', smtpPort: 587, imapHost: '', imapPort: 993, helpUrl: '', helpLabel: '' },
] as const

type ProviderId = typeof PROVIDER_PRESETS[number]['id']

function inferProvider(smtpHost: string): ProviderId {
  const match = PROVIDER_PRESETS.find(p => p.smtpHost && p.smtpHost === smtpHost)
  return (match?.id ?? 'custom') as ProviderId
}

interface EmailAccountRow {
  id: string; name: string; fromAddress: string
  smtpHost: string; smtpPort: number; smtpUser: string
  imapHost?: string; imapPort?: number; imapUser?: string
  isDefault?: boolean; isActive: boolean
}

export function EmailAccountsSettings() {
  usePageTitle('Email Accounts')
  const { companyId } = useParams<{ companyId: string }>()
  const qc = useQueryClient()
  const [showCreate, setShowCreate] = useState(false)
  const [editTarget, setEditTarget] = useState<EmailAccountRow | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['email-accounts', companyId],
    queryFn: () => emailAccountsApi.list(companyId!).then(r => r.data),
    enabled: !!companyId,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => emailAccountsApi.remove(companyId!, id),
    onSuccess: () => {
      toast.success('Email account deleted')
      qc.invalidateQueries({ queryKey: ['email-accounts', companyId] })
      setDeleteTarget(null)
    },
  })

  if (isLoading) return <PageSpinner />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">Email Accounts</h2>
        <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm">
          <Plus className="w-3.5 h-3.5" /> Add Account
        </button>
      </div>
      <div className="card overflow-hidden">
        <table className="table">
          <thead><tr><th>Account Name</th><th>From Email</th><th>SMTP Host</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {(!data || data.length === 0) && <tr><td colSpan={5} className="text-center py-8 text-gray-500 text-sm">No email accounts</td></tr>}
            {data?.map((a: EmailAccountRow) => (
              <tr key={a.id}>
                <td>{a.name}</td>
                <td>{a.fromAddress}</td>
                <td className="text-gray-500">{a.smtpHost}</td>
                <td><span className={`badge ${a.isActive ? 'badge-green' : 'badge-gray'}`}>{a.isActive ? 'Active' : 'Inactive'}</span></td>
                <td className="flex gap-1">
                  <button
                    onClick={() => setEditTarget(a)}
                    className="btn btn-ghost btn-sm p-1 text-gray-600"
                    aria-label="Edit email account"
                    title="Edit email account"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => setDeleteTarget({ id: a.id, label: a.fromAddress })} className="btn btn-ghost btn-sm p-1 text-red-600">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Modal
        open={showCreate || !!editTarget}
        onClose={() => { setShowCreate(false); setEditTarget(null) }}
        title={editTarget ? 'Edit Email Account' : 'Add Email Account'}
        size="lg"
      >
        <EmailAccountForm
          companyId={companyId!}
          existing={editTarget}
          onSuccess={() => { setShowCreate(false); setEditTarget(null); qc.invalidateQueries({ queryKey: ['email-accounts', companyId] }) }}
        />
      </Modal>
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title="Delete email account?"
        message={`This will remove "${deleteTarget?.label}". Outbound sends and IMAP polling will stop immediately.`}
        confirmLabel="Delete"
        isPending={deleteMutation.isPending}
      />
    </div>
  )
}

function EmailAccountForm({ companyId, existing, onSuccess }: {
  companyId: string; existing: EmailAccountRow | null; onSuccess: () => void
}) {
  const defaultProvider: ProviderId = existing ? inferProvider(existing.smtpHost) : 'gmail'
  // Detect if existing account has separate IMAP credentials
  const hasSeparateImap = !!(existing?.imapUser && existing.imapUser !== existing.smtpUser)

  const { register, handleSubmit, watch, setValue, reset } = useForm<{
    provider: ProviderId
    name: string; fromAddress: string
    smtpHost: string; smtpPort: number
    imapHost?: string; imapPort?: number
    username: string; password?: string
    imapUser?: string; imapPass?: string
    isDefault?: boolean; isActive?: boolean
    tlsRejectUnauthorized?: boolean
  }>({
    defaultValues: existing
      ? {
          provider: defaultProvider,
          name: existing.name, fromAddress: existing.fromAddress,
          smtpHost: existing.smtpHost, smtpPort: existing.smtpPort,
          imapHost: existing.imapHost, imapPort: existing.imapPort,
          username: existing.smtpUser,
          imapUser: existing.imapUser,
          isDefault: existing.isDefault, isActive: existing.isActive,
          tlsRejectUnauthorized: existing.tlsRejectUnauthorized ?? true,
        }
      : { provider: 'gmail', smtpHost: 'smtp.gmail.com', smtpPort: 587, imapHost: 'imap.gmail.com', imapPort: 993, tlsRejectUnauthorized: true },
  })

  const [verifyResult, setVerifyResult] = useState<{ smtp: boolean; imap: boolean; smtpError?: string; imapError?: string } | null>(null)
  const [verifying, setVerifying] = useState(false)
  const [separateImap, setSeparateImap] = useState(hasSeparateImap)

  const provider = watch('provider')
  const preset = PROVIDER_PRESETS.find(p => p.id === provider)!
  const isCustom = provider === 'custom'

  const handleProviderChange = (id: ProviderId) => {
    setValue('provider', id)
    const p = PROVIDER_PRESETS.find(pr => pr.id === id)!
    if (id !== 'custom') {
      setValue('smtpHost', p.smtpHost as string)
      setValue('smtpPort', p.smtpPort)
      setValue('imapHost', p.imapHost as string)
      setValue('imapPort', p.imapPort)
    } else {
      setValue('smtpHost', '')
      setValue('imapHost', '')
    }
  }

  const mutation = useMutation({
    mutationFn: async (dto: Record<string, unknown>) => {
      const sharedUser = dto.username as string
      const sharedPass = dto.password as string | undefined
      const imapUser = separateImap ? (dto.imapUser as string | undefined) : sharedUser
      const imapPass = separateImap ? (dto.imapPass as string | undefined) : sharedPass

      setVerifying(true)
      setVerifyResult(null)
      let check: { smtp: boolean; imap: boolean; smtpError?: string; imapError?: string }
      try {
        if (!existing || sharedPass) {
          const res = await emailAccountsApi.verify(companyId, {
            smtpHost: dto.smtpHost, smtpPort: dto.smtpPort,
            smtpUser: sharedUser, smtpPass: sharedPass || '__keep__',
            imapHost: dto.imapHost || undefined, imapPort: dto.imapPort || undefined,
            imapUser: imapUser || undefined,
            imapPass: imapPass || undefined,
            tlsRejectUnauthorized: dto.tlsRejectUnauthorized ?? true,
          })
          check = res.data
        } else {
          check = { smtp: true, imap: true }
        }
      } finally {
        setVerifying(false)
      }
      setVerifyResult(check)
      if (!check.smtp || !check.imap) throw new Error('connectivity_check_failed')

      const payload: Record<string, unknown> = {
        name: dto.name, fromAddress: dto.fromAddress,
        smtpHost: dto.smtpHost, smtpPort: dto.smtpPort, smtpUser: sharedUser,
        imapHost: dto.imapHost || undefined, imapPort: dto.imapPort || undefined,
        imapUser: imapUser || undefined,
        isDefault: dto.isDefault, isActive: dto.isActive,
        tlsRejectUnauthorized: dto.tlsRejectUnauthorized ?? true,
      }
      if (sharedPass) { payload.smtpPass = sharedPass; payload.imapPass = imapPass || sharedPass }
      if (separateImap && imapPass) payload.imapPass = imapPass
      return existing
        ? emailAccountsApi.update(companyId, existing.id, payload)
        : emailAccountsApi.create(companyId, payload)
    },
    onSuccess: () => { reset(); toast.success(existing ? 'Account updated' : 'Account saved'); onSuccess() },
    onError: (err: any) => {
      if (err?.message === 'connectivity_check_failed') return
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : Array.isArray(msg) ? msg.join('. ') : 'Failed to save account')
    },
  })

  return (
    <form onSubmit={handleSubmit(d => mutation.mutate(d as Record<string, unknown>))} className="space-y-3">
      <div>
        <label className="label">Email Provider *</label>
        <select
          {...register('provider')}
          onChange={e => handleProviderChange(e.target.value as ProviderId)}
          className="input"
        >
          {PROVIDER_PRESETS.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
        </select>
      </div>

      {preset.helpUrl && (
        <a href={preset.helpUrl} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-primary-600 hover:underline">
          📖 {preset.helpLabel} →
        </a>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div><label className="label">Account Name *</label><input {...register('name', { required: true })} className="input" placeholder="e.g. Sales Outreach" /></div>
        <div><label className="label">From Email *</label><input {...register('fromAddress', { required: true })} type="email" className="input" /></div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div><label className="label">Username *</label><input {...register('username', { required: true })} className="input" placeholder="your@email.com" /></div>
        <div>
          <label className="label">Password {existing ? '' : '*'}</label>
          <input {...register('password', { required: !existing })} type="password" className="input" placeholder={existing ? 'Leave blank to keep current' : ''} autoComplete="new-password" />
        </div>
      </div>

      <p className="text-xs font-medium text-gray-500 pt-1">Outgoing (SMTP)</p>
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <label className="label">Host *</label>
          <input {...register('smtpHost', { required: true })} className={`input read-only:bg-gray-50 read-only:cursor-default`} readOnly={!isCustom} />
        </div>
        <div>
          <label className="label">Port</label>
          <input {...register('smtpPort', { valueAsNumber: true })} type="number" className={`input read-only:bg-gray-50 read-only:cursor-default`} readOnly={!isCustom} />
        </div>
      </div>

      <p className="text-xs font-medium text-gray-500 pt-1">Incoming (IMAP) <span className="text-gray-400 font-normal">— optional, for receiving</span></p>
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <label className="label">Host</label>
          <input {...register('imapHost')} className={`input read-only:bg-gray-50 read-only:cursor-default`} readOnly={!isCustom} />
        </div>
        <div>
          <label className="label">Port</label>
          <input {...register('imapPort', { valueAsNumber: true })} type="number" className={`input read-only:bg-gray-50 read-only:cursor-default`} readOnly={!isCustom} />
        </div>
      </div>

      <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer select-none">
        <input type="checkbox" checked={separateImap} onChange={e => setSeparateImap(e.target.checked)} className="rounded border-gray-300" />
        Use different credentials for incoming mail
      </label>

      {separateImap && (
        <div className="grid grid-cols-2 gap-3 pl-5 border-l-2 border-gray-200">
          <div><label className="label">IMAP Username</label><input {...register('imapUser')} className="input" /></div>
          <div>
            <label className="label">IMAP Password</label>
            <input {...register('imapPass')} type="password" className="input" placeholder={existing ? 'Leave blank to keep current' : ''} autoComplete="new-password" />
          </div>
        </div>
      )}

      <label className="flex items-center gap-2 text-xs text-gray-500 cursor-pointer select-none">
        <input type="checkbox" {...register('tlsRejectUnauthorized')} className="rounded border-gray-300" />
        <span>
          Verify TLS certificate <span className="text-gray-400 font-normal">(recommended — uncheck only if your mail server uses a self-signed or private CA cert)</span>
        </span>
      </label>

      {existing && (
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" {...register('isActive')} /> Account active
        </label>
      )}

      {/* Connection check results */}
      {(verifying || verifyResult) && (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 space-y-1">
          <p className="text-xs font-medium text-gray-600 mb-1">Connection check</p>
          <div className="flex items-center gap-2 text-xs">
            {verifying
              ? <Loader className="w-3.5 h-3.5 animate-spin text-gray-400" />
              : verifyResult?.smtp
                ? <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                : <XCircle className="w-3.5 h-3.5 text-red-500" />
            }
            <span className={verifyResult && !verifyResult.smtp ? 'text-red-600' : 'text-gray-700'}>
              SMTP {verifying ? 'checking…' : verifyResult?.smtp ? 'connected' : `failed — ${verifyResult?.smtpError}`}
            </span>
          </div>
          {(verifying || verifyResult?.imapError !== undefined || verifyResult?.imap !== undefined) && (
            <div className="flex items-center gap-2 text-xs">
              {verifying
                ? <Loader className="w-3.5 h-3.5 animate-spin text-gray-400" />
                : verifyResult?.imap
                  ? <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                  : <XCircle className="w-3.5 h-3.5 text-red-500" />
              }
              <span className={verifyResult && !verifyResult.imap ? 'text-red-600' : 'text-gray-700'}>
                IMAP {verifying ? 'checking…' : verifyResult?.imap ? 'connected' : `failed — ${verifyResult?.imapError}`}
              </span>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end pt-2">
        <button type="submit" disabled={mutation.isPending || verifying} className="btn btn-primary btn-sm">
          {verifying ? 'Checking connection…' : existing ? 'Save changes' : 'Save Account'}
        </button>
      </div>
    </form>
  )
}

export function EmailTemplatesSettings() {
  usePageTitle('Email Templates')
  const { companyId } = useParams<{ companyId: string }>()
  const qc = useQueryClient()
  const [editItem, setEditItem] = useState<{ id: string; name: string; subject: string; bodyHtml: string } | null>(null)
  const [showCreate, setShowCreate] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; label: string } | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['email-templates', companyId],
    queryFn: () => emailTemplatesApi.list(companyId!).then(r => r.data),
    enabled: !!companyId,
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => emailTemplatesApi.remove(companyId!, id),
    onSuccess: () => {
      toast.success('Template deleted')
      qc.invalidateQueries({ queryKey: ['email-templates', companyId] })
      setDeleteTarget(null)
    },
  })

  if (isLoading) return <PageSpinner />

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">Email Templates</h2>
        <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm"><Plus className="w-3.5 h-3.5" /> New Template</button>
      </div>
      <p className="text-xs text-gray-500">Use variables: <code className="bg-gray-100 px-1 rounded">{'{{lead_name}}'}</code> <code className="bg-gray-100 px-1 rounded">{'{{assignee_name}}'}</code> <code className="bg-gray-100 px-1 rounded">{'{{company_name}}'}</code></p>
      <div className="card overflow-hidden">
        <table className="table">
          <thead><tr><th>Name</th><th>Subject</th><th></th></tr></thead>
          <tbody>
            {(!data || data.length === 0) && <tr><td colSpan={3} className="text-center py-8 text-gray-500 text-sm">No templates</td></tr>}
            {data?.map((t: { id: string; name: string; subject: string; bodyHtml: string }) => (
              <tr key={t.id}>
                <td className="font-medium">{t.name}</td>
                <td className="text-gray-600 text-xs">{t.subject}</td>
                <td className="flex gap-1">
                  <button onClick={() => setEditItem(t)} className="btn btn-ghost btn-sm text-xs">Edit</button>
                  <button onClick={() => setDeleteTarget({ id: t.id, label: t.name })} className="btn btn-ghost btn-sm p-1 text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Modal open={showCreate || !!editItem} onClose={() => { setShowCreate(false); setEditItem(null) }} title={editItem ? 'Edit Template' : 'New Template'} size="lg">
        <TemplateForm
          companyId={companyId!}
          existing={editItem}
          onSuccess={() => { setShowCreate(false); setEditItem(null); qc.invalidateQueries({ queryKey: ['email-templates', companyId] }) }}
        />
      </Modal>
      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        title="Delete email template?"
        message={`This will remove "${deleteTarget?.label}". Sends already in flight aren't affected.`}
        confirmLabel="Delete"
        isPending={deleteMutation.isPending}
      />
    </div>
  )
}

function TemplateForm({ companyId, existing, onSuccess }: {
  companyId: string; existing: { id: string; name: string; subject: string; bodyHtml: string } | null; onSuccess: () => void
}) {
  // Whitelist: when editing, `existing` is the full entity from GET (id,
  // companyId, createdBy, createdAt, updatedAt). React Hook Form keeps
  // unregistered defaultValues in form state and submits them too — and
  // the global ValidationPipe rejects with forbidNonWhitelisted.
  const { register, handleSubmit, reset } = useForm<{ name: string; subject: string; bodyHtml: string }>({
    defaultValues: existing
      ? { name: existing.name, subject: existing.subject, bodyHtml: existing.bodyHtml }
      : {},
  })
  const mutation = useMutation({
    mutationFn: (dto: { name: string; subject: string; bodyHtml: string }) => {
      // Pluck only DTO fields. Belt-and-suspenders against any future drift.
      const payload = { name: dto.name, subject: dto.subject, bodyHtml: dto.bodyHtml }
      return existing
        ? emailTemplatesApi.update(companyId, existing.id, payload)
        : emailTemplatesApi.create(companyId, payload)
    },
    onSuccess: () => { reset(); toast.success(existing ? 'Updated' : 'Created'); onSuccess() },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : Array.isArray(msg) ? msg.join('. ') : 'Failed to save template')
    },
  })
  return (
    <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-3">
      <div><label className="label">Name *</label><input {...register('name', { required: true })} className="input" /></div>
      <div><label className="label">Subject *</label><input {...register('subject', { required: true })} className="input" /></div>
      <div><label className="label">Body (HTML) *</label><textarea {...register('bodyHtml', { required: true })} rows={8} className="input resize-y font-mono text-sm" /></div>
      <div className="flex justify-end pt-2">
        <button type="submit" disabled={mutation.isPending} className="btn btn-primary btn-sm">Save Template</button>
      </div>
    </form>
  )
}
